import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import { API_BASE_URL } from '../config';
import { ChevronLeft, Send, Star } from 'lucide-react';
import './FeedbackPage.css';

const FeedbackPage = () => {
    const { isLoaded, isSignedIn, getToken } = useAuth();
    const navigate = useNavigate();

    const [accuracy, setAccuracy] = useState(0);
    const [ease, setEase] = useState(0);
    const [nps] = useState(0);
    const [comments, setComments] = useState('');
    const [requests, setRequests] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (isLoaded && !isSignedIn) {
            navigate('/login');
        }
    }, [isLoaded, isSignedIn, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = await getToken();
            const response = await fetch(`${API_BASE_URL}/feedback/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    accuracy_rating: accuracy,
                    ease_of_use: ease,
                    would_use_regularly: 'maybe',
                    nps_score: nps || 5,
                    additional_comments: comments,
                    feature_requests: requests
                }),
                credentials: 'include'
            });

            if (response.ok) {
                setSubmitted(true);
            }
        } catch (error) {
            console.error('Feedback submission failed:', error);
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="feedback-page-container">
                <div className="feedback-card glass text-center">
                    <div className="success-icon">✨</div>
                    <h1>Thank You!</h1>
                    <p>Your feedback helps us make VerDict AI better for everyone.</p>
                    <button onClick={() => navigate('/app')} className="back-home-btn">
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="feedback-page-container">
            <div className="feedback-form-card glass">
                <button onClick={() => navigate(-1)} className="back-nav-btn">
                    <ChevronLeft size={20} /> Back
                </button>

                <div className="form-header">
                    <h1>Share Your Feedback</h1>
                    <p>We're building VerDict AI with your input.</p>
                </div>

                <form onSubmit={handleSubmit} className="feedback-form">
                    <div className="rating-section">
                        <label>How accurate was the verification?</label>
                        <div className="star-rating">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    onClick={() => setAccuracy(star)}
                                    className={accuracy >= star ? 'active' : ''}
                                >
                                    <Star fill={accuracy >= star ? "currentColor" : "none"} />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="rating-section">
                        <label>How easy was it to use?</label>
                        <div className="star-rating">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    onClick={() => setEase(star)}
                                    className={ease >= star ? 'active' : ''}
                                >
                                    <Star fill={ease >= star ? "currentColor" : "none"} />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="input-section">
                        <label>What features would you like to see next?</label>
                        <textarea
                            value={requests}
                            onChange={(e) => setRequests(e.target.value)}
                            placeholder="e.g. Browser extension, history, more languages..."
                        />
                    </div>

                    <div className="input-section">
                        <label>Any other comments?</label>
                        <textarea
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Your thoughts on the experience..."
                        />
                    </div>

                    <button type="submit" className="feedback-submit-btn" disabled={loading || !accuracy || !ease}>
                        {loading ? 'Sending...' : 'Submit Feedback'}
                        <Send size={18} />
                    </button>
                </form>
            </div>
        </div>
    );
};

export default FeedbackPage;
